import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";

import DatePicker from '../formsUi/datePicker/DatePicker';
import Label from '../formsUi/common/label/Label';
import ValidationMessage from '../formsUi/common/validation/ValidationMessage';

// Parses date string to a local midnight Date and applies an optional day offset.
// minDateFrom/maxDateFrom in fieldSetting reference another field's dataField by name.
const resolveDateFromISO = (isoStr, offsetDays = 0) => {
    if (!isoStr) return undefined;
    const cleanStr = String(isoStr).split('T')[0];
    let date;
    if (cleanStr.includes('-')) {
        const parts = cleanStr.split('-');
        if (parts.length === 3) {
            const year = Number(parts[0]);
            const month = Number(parts[1]);
            const day = Number(parts[2]);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                date = new Date(year, month - 1, day);
            }
        }
    } else {
        const parsed = new Date(cleanStr);
        if (!isNaN(parsed.getTime())) {
            date = parsed;
        }
    }
    if (!date || isNaN(date.getTime())) return undefined;
    if (offsetDays !== 0) date.setDate(date.getDate() + offsetDays);
    return date;
};

const FormDatePickerControls = ({
    dataField,
    labelName,
    placeholder,
    name,
    value,
    error,
    formSetting,
    overRideProps,
    isRequired,
    fieldSetting,
    onChange,
    onBlur,
    formData,
    ...otherProps
}) => {

    const resolvedFieldSetting = useMemo(() => {
        if (!fieldSetting?.minDateFrom && !fieldSetting?.maxDateFrom) return fieldSetting;
        const resolved = { ...fieldSetting };
        if (fieldSetting.minDateFrom) {
            resolved.minDate = resolveDateFromISO(
                formData?.[fieldSetting.minDateFrom],
                fieldSetting.daysOffset ?? 0
            );
        }
        if (fieldSetting.maxDateFrom) {
            resolved.maxDate = resolveDateFromISO(
                formData?.[fieldSetting.maxDateFrom],
                fieldSetting.maxDaysOffset ?? 0
            );
        }
        return resolved;
    }, [fieldSetting, formData, value]);

    const formatDate = (date) => {
        if (!date) return null;
        // Create a new Date object considering local timezone offset
        // This ensures when we format, we get the correct local day
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - (offset * 60 * 1000));
        return localDate.toISOString().split('T')[0];
    };

    const handleDateChange = useCallback((date) => {
        if (onChange) {
            if (Array.isArray(date)) {
                // For range picker
                const formattedDates = date.map(formatDate);
                onChange(dataField, formattedDates);
            } else {
                // For normal date picker
                onChange(dataField, formatDate(date));
            }
        }
    }, [onChange, dataField]);

    const handleOnBlur = () => {
        onBlur(dataField)
    };

    return (
        <div className="date-picker-label-part">
            <div className="input-label-part">
                {labelName && (
                    <Label labelName={labelName} isRequired={isRequired} />
                )}
            </div>
            {resolvedFieldSetting?.subTitle && (
                <div className="section-title">
                    <h5>{resolvedFieldSetting.subTitle}</h5>
                </div>
            )}
            <div className="input-date-sec">
                <DatePicker
                    label={labelName}
                    id={name}
                    selected={value}
                    name={name}
                    placeholder={resolvedFieldSetting?.placeholder || placeholder}
                    className="datepicker-field"
                    onChange={handleDateChange}
                    onBlur={handleOnBlur}
                    isDisabled={
                        formSetting?.isViewOnly ||
                        resolvedFieldSetting?.isDisable ||
                        overRideProps?.isDisable}
                    datePickerConfig={resolvedFieldSetting}
                    {...otherProps} />
                <ValidationMessage error={error || ""} />
            </div>
        </div>
    );
};

FormDatePickerControls.propTypes = {
    dataField: PropTypes.string.isRequired,
    labelName: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    error: PropTypes.string,
    formSetting: PropTypes.shape({ isViewOnly: PropTypes.bool }),
    overRideProps: PropTypes.shape({ isDisable: PropTypes.bool }),
    isRequired: PropTypes.bool,
    fieldSetting: PropTypes.shape({
        subTitle: PropTypes.string,
        placeholder: PropTypes.string,
        isDisable: PropTypes.bool,
        dateConfig: PropTypes.any
    }),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
};

export default FormDatePickerControls;